/**
 * PIQ 8.2 / 8.3: Executive Brief Primary Card
 * ============================================
 * Visually dominant, scannable exec brief surface.
 * Full-width, stronger typography, no duplicated chips.
 * PIQ 8.3: "What Changed" is a labeled table with clickable rows.
 */

import React, { useState } from 'react';
import {
  FileText,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Zap,
  Ban,
  TrendingUp,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import type { InsightDetailVM } from './InsightDetailDrawer';

// ============================================================
// Types
// ============================================================

interface ExecBriefSection {
  title: string;
  items: string[];
}

interface ExecBriefMetric {
  label: string;
  value: string;
  change?: string;
  changeDirection?: 'up' | 'down' | 'neutral';
}

/** PIQ 8.3: Channel metric for "What Changed" table */
export interface ChannelMetric {
  channel: string;
  channelKey: string;
  revenue: number | null;
  revenueFormatted: string;
  wowPct: number | null;
  wowFormatted: string;
  yoyPct: number | null;
  yoyFormatted: string;
  hasConflict?: boolean;
  evidenceRefs?: string[];
}

export interface ExecBriefData {
  whatMattersNow?: string[];
  doTheseNow?: string[];
  doNotDo?: string[];
  whatChanged?: ExecBriefMetric[];
  /** PIQ 8.3: Structured channel metrics for table display */
  channelMetrics?: ChannelMetric[];
  sections?: ExecBriefSection[];
  generatedAt?: string;
}

interface ExecBriefPrimaryCardProps {
  data: ExecBriefData | null;
  isLoading?: boolean;
  /** Callback when user clicks Generate Brief */
  onGenerateBrief?: () => void;
  /** PIQ 8.3: Callback when user clicks a metric row */
  onMetricClick?: (detail: InsightDetailVM) => void;
  /** PIQ 8.5: Show "Why this is true" drivers summary */
  showDriversSummary?: boolean;
  /** PIQ 8.5.2: BrainContract for deterministic drivers summary */
  brainContract?: {
    retailerAttribution?: {
      primaryDriver?: { retailer: string; contributionPct: number };
    };
    retailerCausality?: {
      classification?: string;
      driverRetailer?: string;
    };
    microAttribution?: Record<string, { topSkus?: any[] }>;
    // PIQ 8.5.2: Additional fields for deterministic "Why" logic
    computedExpectedValue?: {
      metricsEvaluated?: number;
      totalUnexpectedDelta?: number;
    };
    seasonalityContext?: {
      withinExpected?: boolean;
      currentPhase?: string;
    };
    forecastExpectation?: {
      status?: string; // 'within_expected_error_band' | 'outside_band' | etc
      errorBandPct?: number;
    };
  };
}

// ============================================================
// PIQ 8.3: Helpers for "What Changed" table
// ============================================================

function formatPct(pct: number | null | undefined | string): string {
  // PIQ 8.3.1: Robust handling - value might be string, null, undefined, or object
  if (pct === null || pct === undefined || pct === '') return '—';
  const num = typeof pct === 'number' ? pct : parseFloat(String(pct));
  if (isNaN(num)) return '—';
  const sign = num >= 0 ? '+' : '';
  return `${sign}${num.toFixed(1)}%`;
}

function getPctColor(pct: number | null | undefined | string): string {
  // PIQ 8.3.1: Robust handling - value might be string, null, undefined, or object
  if (pct === null || pct === undefined || pct === '') return 'text-gray-400';
  const num = typeof pct === 'number' ? pct : parseFloat(String(pct));
  if (isNaN(num)) return 'text-gray-400';
  if (num > 0) return 'text-emerald-600';
  if (num < 0) return 'text-red-600';
  return 'text-gray-500';
}

function buildMetricDetailVM(metric: ChannelMetric): InsightDetailVM {
  return {
    id: `metric-${metric.channelKey}`,
    title: `${metric.channel} Performance`,
    category: 'opportunities',
    categoryLabel: 'Channel Metric',
    confidence: null,
    impactBand: metric.revenueFormatted,
    why: `${metric.channel} shows ${metric.wowPct !== null ? `${formatPct(metric.wowPct)} WoW` : 'no WoW data'} and ${metric.yoyPct !== null ? `${formatPct(metric.yoyPct)} YoY` : 'no YoY data'}.`,
    evidenceRefs: (metric.evidenceRefs || []).map(ref => ({
      label: ref,
      type: 'metric' as const,
      ref,
    })),
    context: {},
    sourceType: 'piq',
  };
}

// ============================================================
// Component
// ============================================================

export function ExecBriefPrimaryCard({ 
  data, 
  isLoading, 
  onGenerateBrief, 
  onMetricClick,
  showDriversSummary = false,
  brainContract,
}: ExecBriefPrimaryCardProps) {
  const [showMetrics, setShowMetrics] = useState(true); // PIQ 8.3: Default expanded

  // PIQ 9.0 Task 4: Rewrite "Why this is true" - max 2 bullets, driver-first
  // RULES:
  // - Max 2 bullets
  // - Priority 1: Primary driver (retailer or SKU with % contribution)
  // - Priority 2: Mechanism (causality / phase / constraint)
  // - NO generic phrases, NO restating belief name, NO metrics without context
  const driversBullets: string[] = [];
  if (showDriversSummary && brainContract) {
    const primaryDriver = brainContract.retailerAttribution?.primaryDriver;
    const causality = brainContract.retailerCausality;
    const microAttr = brainContract.microAttribution;
    const cev = brainContract.computedExpectedValue;
    const seasonality = brainContract.seasonalityContext;
    const forecast = brainContract.forecastExpectation;
    
    // BULLET 1 (REQUIRED): Primary driver with contribution %
    if (primaryDriver && primaryDriver.contributionPct > 0.1) {
      const label = primaryDriver.retailer.charAt(0).toUpperCase() + primaryDriver.retailer.slice(1);
      const pct = Math.round(primaryDriver.contributionPct * 100);
      const direction = (cev?.totalUnexpectedDelta ?? 0) < 0 ? 'downside' : 'variance';
      driversBullets.push(`${label} accounts for ${pct}% of ${direction}`);
    } else if (microAttr) {
      // Fallback to micro-attribution SKU driver
      const keys = Object.keys(microAttr);
      if (keys.length > 0) {
        const topSkus = microAttr[keys[0]]?.topSkus || [];
        if (topSkus.length > 0 && topSkus[0]?.description) {
          const contribution = Math.round((topSkus[0].contributionPct || topSkus[0].contribution || 0) * 100);
          if (contribution > 10) {
            driversBullets.push(`${topSkus[0].description} drives ${contribution}% of movement`);
          }
        }
      }
    }
    
    // BULLET 2 (OPTIONAL): Mechanism (causality/phase/constraint)
    if (driversBullets.length >= 1) {
      // Add mechanism bullet
      if (causality?.classification === 'leader_follower' && causality?.driverRetailer) {
        const label = causality.driverRetailer.charAt(0).toUpperCase() + causality.driverRetailer.slice(1);
        driversBullets.push(`${label} leads other retailers by 1-2 weeks`);
      } else if (causality?.classification === 'synchronized') {
        driversBullets.push('Movement synchronized (external market factor)');
      } else if (seasonality?.currentPhase) {
        const phase = seasonality.currentPhase.replace(/_/g, ' ').toLowerCase();
        driversBullets.push(`Currently in ${phase} phase`);
      }
    }
    
    // If NO driver found, provide context-specific reason (not generic)
    if (driversBullets.length === 0) {
      const metricsEvaluated = cev?.metricsEvaluated ?? 0;
      
      if (metricsEvaluated === 0) {
        driversBullets.push('Snapshot incomplete — no evaluable metrics');
      } else if (seasonality?.withinExpected === true) {
        const phase = seasonality.currentPhase ? ` ${seasonality.currentPhase.replace(/_/g, ' ')}` : '';
        driversBullets.push(`Performance within${phase} seasonal range`);
      } else if (forecast?.status === 'within_expected_error_band') {
        driversBullets.push('Movement within forecast error band');
      } else {
        // Only if truly nothing available
        driversBullets.push('Channel movements offset each other');
      }
    }
  }
  
  // PIQ 9.0: STRICT cap to max 2 bullets
  const displayDriversBullets = driversBullets.slice(0, 2);

  if (isLoading) {
    return (
      <Card className="mb-6 border-2 border-violet-200 bg-gradient-to-br from-white to-violet-50">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-100 rounded-lg">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
            <CardTitle className="text-lg font-bold text-gray-900">Executive Brief</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className="mb-6 border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <FileText className="w-5 h-5 text-gray-400" />
            </div>
            <CardTitle className="text-lg font-bold text-gray-900">Executive Brief</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">
              No brief generated for this week yet.
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Generate a brief to see key insights, recommended actions, and what to avoid.
            </p>
            <button
              onClick={onGenerateBrief}
              disabled={!onGenerateBrief}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Zap className="w-4 h-4" />
              Generate Brief
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const whatMatters = data.whatMattersNow?.slice(0, 3) || [];
  const doThese = data.doTheseNow?.slice(0, 3) || [];
  const doNot = data.doNotDo?.slice(0, 3) || [];
  const metrics = data.whatChanged || [];
  const channelMetrics = data.channelMetrics || [];

  // PIQ 9.0 Task 7: Cleaner styling - no gradients, tight spacing
  return (
    <Card className="border border-gray-200 bg-white shadow-sm">
      <CardHeader className="pb-1.5 pt-2.5 px-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <FileText className="w-4 h-4 text-violet-600" />
            <CardTitle className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Executive Brief</CardTitle>
          </div>
          {data.generatedAt && (
            <span className="text-[10px] text-gray-400">
              {new Date(data.generatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-2 pb-2.5 px-3">
        {/* PIQ 9.0: What Matters Now - tighter spacing */}
        {whatMatters.length > 0 && (
          <div>
            <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
              What Matters Now
            </h3>
            <ul className="space-y-0.5">
              {whatMatters.map((item, idx) => (
                <li key={idx} className="flex items-start gap-1.5 text-sm text-gray-700">
                  <span className="text-violet-500 font-medium text-xs mt-0.5">{idx + 1}.</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* PIQ 9.0: Do These Now - no gradient, just border */}
        {doThese.length > 0 && (
          <div className="rounded border border-emerald-200 bg-emerald-50/50 p-2">
            <h3 className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-1">
              Do These Now
            </h3>
            <ul className="space-y-0.5">
              {doThese.map((item, idx) => (
                <li key={idx} className="flex items-start gap-1.5 text-sm text-emerald-800">
                  <span className="text-emerald-500 font-bold mt-0.5">→</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* PIQ 9.0: Do Not Do - neutral styling */}
        {doNot.length > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50/40 p-2">
            <h3 className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">
              Do Not Do
            </h3>
            <ul className="space-y-0.5">
              {doNot.map((item, idx) => (
                <li key={idx} className="flex items-start gap-1.5 text-sm text-amber-800">
                  <span className="text-amber-500 text-xs mt-0.5">✕</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* PIQ 9.0: What Changed - Compact table */}
        {(channelMetrics.length > 0 || metrics.length > 0) && (
          <div className="border-t border-gray-100 pt-2">
            <button
              onClick={() => setShowMetrics(!showMetrics)}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors w-full"
            >
              <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                What Changed
              </h3>
              <span className="text-[10px] text-gray-400">
                ({channelMetrics.length || metrics.length})
              </span>
              <span className="ml-auto">
                {showMetrics ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </span>
            </button>

            {showMetrics && channelMetrics.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Channel</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Revenue</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">WoW %</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">YoY %</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelMetrics.map((metric, idx) => (
                      <tr
                        key={metric.channelKey}
                        onClick={() => onMetricClick?.(buildMetricDetailVM(metric))}
                        className={`border-b border-gray-100 hover:bg-violet-50 cursor-pointer transition-colors group ${
                          metric.hasConflict ? 'bg-amber-50/50' : ''
                        }`}
                      >
                        <td className="py-2.5 px-3 font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            {metric.channel}
                            {metric.hasConflict && (
                              <span className="text-amber-500" title="Data conflict detected">
                                <AlertCircle className="w-3.5 h-3.5" />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-right font-semibold text-gray-900">
                          {metric.revenueFormatted}
                        </td>
                        <td className={`py-2.5 px-3 text-right font-medium ${getPctColor(metric.wowPct)}`}>
                          {metric.wowPct !== null ? formatPct(metric.wowPct) : (
                            <span className="text-gray-400 cursor-help" title="Not available for this week">—</span>
                          )}
                        </td>
                        <td className={`py-2.5 px-3 text-right font-medium ${getPctColor(metric.yoyPct)}`}>
                          {metric.yoyPct !== null ? formatPct(metric.yoyPct) : (
                            <span className="text-gray-400 cursor-help" title="Not available for this week">—</span>
                          )}
                        </td>
                        <td className="py-2.5 px-1">
                          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-violet-500 transition-colors" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Fallback to old metrics if no channel metrics */}
            {showMetrics && channelMetrics.length === 0 && metrics.length > 0 && (
              <div className="mt-3 grid gap-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {metrics.map((metric, idx) => (
                  <div key={idx} className="bg-white rounded-lg p-3 border border-gray-100">
                    <p className="text-xs text-gray-500 truncate">{metric.label}</p>
                    <p className="text-sm font-semibold text-gray-900">{metric.value}</p>
                    {metric.change && (
                      <p className={`text-xs font-medium ${
                        metric.changeDirection === 'up' ? 'text-emerald-600' :
                        metric.changeDirection === 'down' ? 'text-red-600' :
                        'text-gray-500'
                      }`}>
                        {metric.change}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PIQ 9.0 Task 4: Why this is true - max 2 bullets, driver-first */}
        {showDriversSummary && displayDriversBullets.length > 0 && (
          <div className="border-t border-gray-100 pt-1.5">
            <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Why
            </h3>
            <ul className="space-y-0">
              {displayDriversBullets.map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-1 text-xs text-gray-500">
                  <span className="text-gray-300 mt-0.5">•</span>
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ExecBriefPrimaryCard;
